import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeCheck, Wallet, ArrowRight, CarFront } from "lucide-react";
import { fileSrc } from "@/lib/api";
import SalePrice from "@/components/SalePrice";
import BenefitBadge from "@/components/BenefitBadge";
import { useBenefits, benefitsFor } from "@/hooks/useBenefits";

export default function CategoryCard({ category, meta }) {
  const benefits = benefitsFor(useBenefits(), { categoryId: category.id });
  const cover = meta?.cover_url ? fileSrc(meta.cover_url) : null;
  const count = meta?.vehicle_count ?? 0;
  const available = meta?.available_count ?? 0;

  return (
    <motion.article
      data-testid={`category-card-${category.id}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45 }}
      className="card-lux overflow-hidden flex flex-col h-full"
    >
      <div className="relative h-60 overflow-hidden bg-[#0D0F14]">
        {cover ? (
          <img
            src={cover}
            alt={`${category.name} ${category.years} — ტაქსის მანქანის ქირაობა თბილისში`}
            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-gray-600">
            <CarFront className="w-10 h-10" />
            <span className="text-xs">ფოტო მალე დაემატება</span>
          </div>
        )}
        <span className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold bg-[#0A0B0E]/80 backdrop-blur text-[#E5C378] border border-[#D4AF37]/40">
          {category.badge}
        </span>
        <div
          data-testid={`category-count-${category.id}`}
          className="absolute bottom-3 right-3 px-3 py-1.5 rounded-xl text-xs font-semibold bg-[#0A0B0E]/85 backdrop-blur border border-white/20 text-right leading-tight"
        >
          <p className="text-white">{count} ავტომობილი</p>
          <p className="text-green-400">{available} ხელმისაწვდომია</p>
        </div>
      </div>
      <div className="p-6 flex flex-col flex-1">
        <h3 className="text-xl font-semibold text-white">
          {category.name} <span className="text-gray-400 font-normal">{category.years}</span>
        </h3>
        <p className="text-gray-400 text-sm mt-2 leading-relaxed line-clamp-2">{category.tagline}</p>
        {benefits.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`category-benefits-${category.id}`}>
            {benefits.map((b) => (
              <BenefitBadge key={b.id} benefit={b} />
            ))}
          </div>
        )}
        <div className="flex items-center justify-between mt-5 pt-5 border-t border-white/10">
          <div>
            <SalePrice original={category.priceLabel} sale={category.sale_price_label} enabled={category.sale_enabled} />
            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
              <Wallet className="w-3.5 h-3.5" /> დეპოზიტი: {category.depositLabel}
            </p>
          </div>
          <BadgeCheck className="w-6 h-6 text-[#D4AF37]" />
        </div>
        <Link
          to={`/category/${category.id}`}
          data-testid={`category-card-cta-${category.id}`}
          className="btn-gold w-full mt-6"
        >
          ავტომობილების ნახვა <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </motion.article>
  );
}
